import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  AccountMeta,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
} from "@solana/web3.js";
import { round } from "mathjs";
import {
  buyNowMagicEden,
  cancelSellTradeMagicEden,
  getNftDataFromMagicEden,
} from "../../../api/magic-eden.api";
import { saveCancellationReason } from "../../../api/proposals.api";
import { CHAIN_ID } from "../../../common/constants/common.constants";

import {
  CREATOR_FEE_ROYALTY,
  ITEM_DETAILS,
  MAGIC_EDEN_FEE,
  MAGIC_EDEN_WEBSITE_URL,
} from "../../../common/constants/magic-eden.constants";
import {
  OfferStatus,
  ProposalAction,
} from "../../../common/enums/proposals.enums";
import { ICancellationReasonDto } from "../../../common/interfaces/club-application.interface";
import {
  IMagicEdenNftData,
  IMagicEdenSellDto,
} from "../../../common/interfaces/magic-eden.interface";
import { IProposalWithMetaData } from "../../../common/interfaces/proposals.interface";
import { distributeProfitFromProposal } from "../../methods/proposals";
import { sendTransaction } from "../../transactions/sendTransaction";

import {
  acceptOfferSeed,
  auctionHouseAddressKey,
  escrowProgramFactory,
  escrowSeed,
  ESCRROW_PROGRAM,
  governanceSeed,
  magicEden,
  programFactory,
  RPC_CONNECTION,
  splGovernanceProgram,
  unqClubSeed,
  vaultSeed,
} from "../utilities";
import { mapErrorsFromApi } from "./common.helpers";

/**
 *
 * @param proposalWithMetaData  Proposal with metadata
 * @returns Magic Eden sell DTO used to interact with our API
 */
export const makeSellMagicEdenDto = async (
  wantedAmount: number,
  nftMint: string,
  tokenAccount: string,
  vaultAddress: PublicKey
): Promise<IMagicEdenSellDto> => {
  return {
    seller: vaultAddress.toString(),
    auctionHouseAddress: auctionHouseAddressKey,
    price: wantedAmount / LAMPORTS_PER_SOL,
    tokenMint: nftMint,
    tokenAccount: tokenAccount,
  };
};
/**
 *
 * @param magicEdenUrl Url copied from magic eden platform
 * @returns Parsed string of mint
 */
export const getMintFromMagicEdenUrl = (magicEdenUrl: string) => {
  let nftMint = magicEdenUrl.includes("/")
    ? magicEdenUrl.split("/")[4]
    : magicEdenUrl;
  if (nftMint.includes("?")) {
    nftMint = nftMint.split("?")[0];
  }
  return nftMint;
};
/**
 *
 * @param mint Mint that will be validated
 * @returns {true-if mint is valid,false-if not valid}
 */
export const validateMintFromUrl = (mint: string) => {
  try {
    new PublicKey(mint);
    return true;
  } catch (error) {
    return false;
  }
};

export async function executeBuyMagicEden(
  offer: PublicKey,
  nftMint: PublicKey
) {
  try {
    const escrowProgram = escrowProgramFactory();
    const [escrowWantedTa] = await PublicKey.findProgramAddress(
      [escrowSeed, offer.toBuffer(), acceptOfferSeed],
      escrowProgram.programId
    );
    const txBuffer = await buyNowMagicEden(
      escrowWantedTa.toString(),
      nftMint.toString()
    );
    const tx = Transaction.from(txBuffer);
    return tx;
  } catch (error) {
    throw error;
  }
}
/**
 *
 * @param nftMint Mint of nft whose price is checked
 * @param offeredAmount Amount written inside proposal instruction
 * @returns
 */
export async function compareBuyPrices(
  nftMint: PublicKey,
  offeredAmount: number
) {
  try {
    const nftStats = await getNftDataFromMagicEden(nftMint.toString());

    if (!nftStats.listing[0]) {
      return;
    }

    if (nftStats.listing[0].price !== offeredAmount / LAMPORTS_PER_SOL) {
      throw new Error(
        `Price of NFT on Magic Eden is updated ${nftStats.listing[0].price} and is different from price from proposal!`
      );
    }
    return nftStats;
  } catch (error: any) {
    return await mapErrorsFromApi(error);
  }
}
/**
 * Cancels successfully executed proposal and returns NFT in vault
 * @param nftMint
 * @param offer
 */
export async function cancelMagicEdenSell(
  nftMint: PublicKey,
  offer: PublicKey,
  wallet: AnchorWallet,
  treasury: PublicKey,
  realmName: string,
  cancelSellDto: ICancellationReasonDto
) {
  try {
    const escrowProgram = escrowProgramFactory();
    const clubProgram = programFactory();

    const [escrowWantedTa] = await PublicKey.findProgramAddress(
      [escrowSeed, offer.toBuffer(), acceptOfferSeed],
      escrowProgram.programId
    );

    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    const txBuffer = await cancelSellTradeMagicEden(nftMint.toString());
    const tx = Transaction.from(txBuffer);

    const executeMeSellIx = clubProgram.instruction.executeMeSellCancel(
      tx.instructions[0].data,
      {
        accounts: {
          payer: wallet.publicKey,
          clubData,
          treasury,
          vault: vaultAddress,
          offer,
          nftMint: tx.instructions[0].keys[3].pubkey,
          offeredNftToken: tx.instructions[0].keys[2].pubkey,
          magicEdenExerchina: tx.instructions[0].keys[1].pubkey,
          autExer: tx.instructions[0].keys[4].pubkey,
          e8cExer: tx.instructions[0].keys[5].pubkey,
          magicEdenState2: tx.instructions[0].keys[7].pubkey,
          magicEdenState1: tx.instructions[0].keys[6].pubkey,
          onebwExer: tx.instructions[0].keys[9].pubkey,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: SystemProgram.programId,
          escrowProgram: ESCRROW_PROGRAM,
          magicEden,
        },
      }
    );
    const newTx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    });

    newTx.add(executeMeSellIx);
    const signedTx = await wallet.signTransaction(newTx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
    await saveCancellationReason(cancelSellDto);
  } catch (error) {
    console.log(error);
    throw error;
  }
}
/**
 *
 * @param nftMint Mint of NFT to check if sale happened
 * @returns
 */
export const checkIfNftSoldOnMagicEden = async (
  nftMint: PublicKey,
  vaultAddress: PublicKey
) => {
  try {
    const nftStats = await getNftDataFromMagicEden(nftMint.toString());

    if (
      nftStats.listing[0] &&
      nftStats.listing[0].seller === vaultAddress.toString()
    ) {
      return false;
    }
    return true;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
/**
 * Distributes profit after successfull Magic Eden sell trade
 * @param proposalWithMetaData
 * @param realmName
 * @param wallet
 * @param treasury
 */
export const distributeProfitAfterMarketplaceSale = async (
  proposalWithMetaData: IProposalWithMetaData,
  realmName: string,
  wallet: AnchorWallet,
  treasury: PublicKey
) => {
  try {
    await distributeProfitFromProposal(
      proposalWithMetaData.proposalBasicData,
      wallet,
      treasury,
      realmName,
      ProposalAction.SellNft,
      proposalWithMetaData.governanceAccount,
      wallet,
      undefined, //TODO@milica-monke: add denom curr
      true
    );
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const substractMagicEdenFees = (
  nftData: any,
  price: number,
  status: string
) => {
  let totalAmount = 0;
  if (status === OfferStatus.initializeOnMe) {
    const magicEdenFee = price * MAGIC_EDEN_FEE;

    const creatorsRoyalty =
      price * (nftData.nft.sellerFeeBasisPoints / CREATOR_FEE_ROYALTY);

    totalAmount = price - creatorsRoyalty - magicEdenFee;
  } else {
    totalAmount = price;
  }

  return totalAmount;
};

/**
 * Opens Magic Eden in new tab, with specific NFT loaded
 * @param nftMint Mint of NFT that is listed on Magic Eden
 */
export const openNftOnMagicEden = (nftMint: string) => {
  window.open(`${MAGIC_EDEN_WEBSITE_URL}${ITEM_DETAILS}/${nftMint}`, "_blank");
};

export async function getCreateProposalMetadataRAForBuyNow(
  meTx: Transaction,
  offer: PublicKey,
  magicEdenAccountsHolder: PublicKey
) {
  const escrowProgram = escrowProgramFactory();
  const [escrowWantedTa] = await PublicKey.findProgramAddress(
    [escrowSeed, offer.toBuffer(), acceptOfferSeed],
    escrowProgram.programId
  );
  const initializeMeAccounts: AccountMeta[] = [
    {
      pubkey: magicEdenAccountsHolder,
      isSigner: false,
      isWritable: true,
    },
    {
      pubkey: offer,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: meTx.instructions[2].keys[4].pubkey,
      isSigner: false,
      isWritable: false,
    },

    {
      pubkey: escrowWantedTa,
      isSigner: false,
      isWritable: false,
    },
  ];

  return { initializeMeAccounts };
}

export async function getCreateProposalMetadataRAForSell(
  meTx: Transaction,
  makerWantedToken: PublicKey,
  offer: PublicKey,
  magicEdenData: PublicKey
) {
  const escrowProgram = escrowProgramFactory();
  const [escrowWantedTa] = await PublicKey.findProgramAddress(
    [escrowSeed, offer.toBuffer(), acceptOfferSeed],
    escrowProgram.programId
  );
  const initializeMeAccounts: AccountMeta[] = [
    {
      pubkey: magicEdenData,
      isSigner: false,
      isWritable: true,
    },
    {
      pubkey: offer,
      isSigner: false,
      isWritable: true,
    },
    {
      pubkey: meTx.instructions[0].keys[4].pubkey,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: makerWantedToken,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: escrowWantedTa,
      isSigner: false,
      isWritable: true,
    },
    {
      pubkey: TOKEN_PROGRAM_ID,
      isSigner: false,
      isWritable: false,
    },
  ];

  return { initializeMeAccounts };
}
export function getExecuteMeTransactionAccounts(
  meTx: Transaction,
  payer: PublicKey,
  clubData: PublicKey,
  offer: PublicKey,
  magicEdenAccountsHolder: PublicKey,
  unqEscrowToken: PublicKey,
  proposalMetadata: PublicKey,
  treasuryData: PublicKey
) {
  return {
    payer,
    clubData,
    treasury: meTx.instructions[2].keys[0].pubkey,
    treasuryData,
    escrowProgram: ESCRROW_PROGRAM,
    offer,
    magicEdenAccountsHolder,
    sellersWallet: meTx.instructions[2].keys[1].pubkey,
    magicEdenExerchina: meTx.instructions[2].keys[2].pubkey,
    magicEdenEscrowedToken: meTx.instructions[2].keys[3].pubkey,
    magicEdenEscrowedTokenMint: meTx.instructions[2].keys[4].pubkey,
    magicEdenEscrowedTokenMetadata: meTx.instructions[2].keys[5].pubkey,
    magicEdenState1: meTx.instructions[2].keys[6].pubkey,
    escrowedWantedToken: meTx.instructions[2].keys[7].pubkey,
    autExer: meTx.instructions[2].keys[8].pubkey,
    e8cExer: meTx.instructions[2].keys[9].pubkey,
    expoitExer: meTx.instructions[2].keys[10].pubkey,
    magicEdenState2: meTx.instructions[2].keys[11].pubkey,
    magicEdenState3: meTx.instructions[2].keys[13].pubkey,
    account13: meTx.instructions[2].keys[12].pubkey,
    account15: meTx.instructions[2].keys[14].pubkey,
    onebwExer: meTx.instructions[2].keys[18].pubkey,
    proposalMetadata,
    tokenProgram: TOKEN_PROGRAM_ID,
    associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
    systemProgram: SystemProgram.programId,
    rent: SYSVAR_RENT_PUBKEY,
    magicEden: new PublicKey("M2mx93ekt1fmXSVkTrUL9xVFHkmME8HTUi5Cyc5aF7K"),
    unqEscrowToken,
  };
}

export function getExecuteMeTransactionRemainingAccounts(meTx: Transaction) {
  let remainingAccounts: {
    pubkey: PublicKey;
    isSigner: boolean;
    isWritable: boolean;
  }[] = [];

  if (meTx.instructions[2].keys[20]) {
    for (const pk of meTx.instructions[2].keys.slice(20)) {
      remainingAccounts.push({
        pubkey: pk.pubkey,
        isSigner: false,
        isWritable: true,
      });
    }
  }

  return remainingAccounts;
}

export function getMagicEdenExecuteSell(
  meTx: Transaction,
  payer: PublicKey,
  clubData: PublicKey,
  treasury: PublicKey,
  vault: PublicKey,
  offer: PublicKey,
  offeredNftToken: PublicKey,
  treasuryData: PublicKey
) {
  return {
    payer,
    clubData,
    treasury,
    vault,
    offer,
    offeredNftToken,
    nftMint: meTx.instructions[0].keys[4].pubkey,
    nftMetadata: meTx.instructions[0].keys[5].pubkey,
    magicEdenExerchina: meTx.instructions[0].keys[1].pubkey,
    autExer: meTx.instructions[0].keys[6].pubkey,
    e8cExer: meTx.instructions[0].keys[7].pubkey,
    magicEdenState1: meTx.instructions[0].keys[8].pubkey,
    magicEdenState2: meTx.instructions[0].keys[9].pubkey,
    onebwExer: meTx.instructions[0].keys[13].pubkey,
    escrowProgram: ESCRROW_PROGRAM,
    treasuryData,
    meState0: meTx.instructions[0].keys[3].pubkey,
    tokenProgram: TOKEN_PROGRAM_ID,
    associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
    systemProgram: SystemProgram.programId,
    rent: SYSVAR_RENT_PUBKEY,
    magicEden,
  };
}

export const calculateMagicEdenStats = (
  price: number,
  nftData: any,
  collectionData: any
) => {
  if (collectionData && nftData && price > 0) {
    const floorPrice = collectionData.floorPrice / LAMPORTS_PER_SOL;
    const magicEdenFee = price * MAGIC_EDEN_FEE;

    const creatorsRoyalty =
      price * (nftData.nft.sellerFeeBasisPoints / CREATOR_FEE_ROYALTY);

    const totalAmount = price - creatorsRoyalty - magicEdenFee;

    const magicEdenStats: IMagicEdenNftData = {
      creatorsRoyalty: creatorsRoyalty,
      floorPrice: floorPrice,
      magicEdenFee: magicEdenFee,
      totalAmount: round(totalAmount),
    };
    return magicEdenStats;
  }
};
